/**
 * Desktop update control. A small fixed toast (bottom-right) appears while the
 * app is RUNNING once a newer signed build has been downloaded in the background:
 * "Restart now" installs it and relaunches (one restart), "Later" hides it for a
 * few hours. Nothing installs without that click. A user-initiated check (menu
 * "Check for Updates…") also shows "up to date", progress and errors; background
 * checks stay silent unless there is something to install.
 * Renders nothing in the browser or when already up to date.
 */
import { useEffect, useState } from 'react';
import { useDesktopUpdater } from '@/lib/desktopUpdater';
import { Button } from '@/components/ui/button';
import { Download, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { RELEASE_NOTES_URL, type ReleaseNote } from '@/lib/releaseNotes';

export function DesktopUpdateBanner() {
  const { status, version, body, error, progress, manual, snoozed, install, check, snooze, isDesktop } =
    useDesktopUpdater();
  // Incoming-version release notes, fetched from the published feed (the running
  // bundle predates the new version, so it can't have them baked in). Falls back
  // to the updater's `body` (latest.json notes).
  const [incomingNotes, setIncomingNotes] = useState<ReleaseNote | null>(null);
  const offering = status === 'ready' || status === 'available';

  useEffect(() => {
    if (!offering || !version) {
      setIncomingNotes(null);
      return;
    }
    let cancelled = false;
    fetch(`${RELEASE_NOTES_URL}?cb=${version}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const v = version.replace(/^v/, '');
        const n = data[v] as ReleaseNote | undefined;
        if (n?.items?.length) setIncomingNotes(n);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [offering, version]);

  // Native menu "Check for Updates…" → a manual check with visible feedback.
  useEffect(() => {
    if (!isDesktop) return;
    let unlisten: (() => void) | undefined;
    import('@tauri-apps/api/event')
      .then(({ listen }) => listen('menu://check-updates', () => void check()))
      .then((fn) => {
        unlisten = fn;
      })
      .catch(() => {});
    return () => unlisten?.();
  }, [isDesktop, check]);

  // The "up to date" confirmation of a manual check fades after a few seconds.
  const [showUpToDate, setShowUpToDate] = useState(false);
  useEffect(() => {
    if (manual && status === 'uptodate') {
      setShowUpToDate(true);
      const t = setTimeout(() => setShowUpToDate(false), 4000);
      return () => clearTimeout(t);
    }
    setShowUpToDate(false);
  }, [manual, status]);

  if (!isDesktop) return null;

  if (status === 'uptodate' && showUpToDate) {
    return (
      <div
        data-testid="desktop-update-uptodate"
        className="fixed bottom-4 right-4 z-[9999] flex w-80 items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800"
      >
        <CheckCircle className="h-5 w-5 text-green-600" />
        <p className="text-sm text-gray-900 dark:text-white">WeaveIn is up to date.</p>
      </div>
    );
  }

  // Background download / failed background check: quiet unless the user asked.
  const visible =
    (offering && !snoozed) ||
    status === 'installing' ||
    (status === 'downloading' && manual) ||
    (status === 'error' && manual);
  if (!visible) return null;

  return (
    <div
      data-testid="desktop-update-banner"
      data-status={status}
      className="fixed bottom-4 right-4 z-[9999] w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      {offering && (
        <>
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-[#5B4B8A]" />
            <p className="font-semibold text-gray-900 dark:text-white">
              {status === 'ready' ? 'Update ready' : 'Update available'}
            </p>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {status === 'ready'
              ? `WeaveIn ${version} is downloaded. Restart to start using it.`
              : `WeaveIn ${version} is ready to install.`}
          </p>
          {incomingNotes?.items?.length ? (
            <ul className="mt-2 max-h-32 space-y-1 overflow-auto">
              {incomingNotes.items.map((item, i) => (
                <li key={i} className="flex gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                  <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-[#5B4B8A]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : body ? (
            <p className="mt-2 max-h-32 overflow-auto whitespace-pre-line text-xs text-gray-600 dark:text-gray-300">
              {body}
            </p>
          ) : null}
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={snooze} data-testid="desktop-update-later">
              Later
            </Button>
            <Button size="sm" onClick={() => void install()} data-testid="desktop-update-restart">
              {status === 'ready' ? 'Restart now' : 'Install & Restart'}
            </Button>
          </div>
        </>
      )}

      {(status === 'downloading' || status === 'installing') && (
        <>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin text-[#5B4B8A]" />
            <p className="font-semibold text-gray-900 dark:text-white">
              {status === 'installing' ? 'Installing…' : `Downloading ${version}…`}
            </p>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full bg-[#5B4B8A] transition-all"
              style={{ width: `${status === 'installing' ? 100 : progress}%` }}
            />
          </div>
          <p className="mt-1 text-right text-xs text-gray-500 dark:text-gray-400">
            {status === 'installing' ? 'Restarting shortly…' : `${progress}%`}
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="font-semibold text-gray-900 dark:text-white">Update failed</p>
          </div>
          <p className="mt-1 break-words text-xs text-gray-500 dark:text-gray-400">{error}</p>
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => void check()}>
              Retry
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
